import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2 } from 'lucide-react';
import { Slot } from 'radix-ui';
import * as React from 'react';
import { cn } from '../../lib/cn.js';

export const buttonVariants = cva(
  [
    'inline-flex select-none items-center justify-center gap-2 whitespace-nowrap rounded-control font-medium',
    'transition-colors duration-[var(--cm-motion-fast)]',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus',
    'disabled:pointer-events-none disabled:opacity-50 aria-busy:cursor-progress',
    '[&_svg]:size-[18px] [&_svg]:shrink-0',
  ],
  {
    variants: {
      variant: {
        primary: 'bg-brand-primary text-brand-on-primary hover:bg-brand-hover active:bg-brand-hover',
        secondary:
          'border border-border-control bg-surface-default text-text-primary hover:border-border-control-strong hover:bg-surface-subtle active:bg-surface-canvas',
        ghost: 'text-text-secondary hover:bg-surface-subtle hover:text-text-primary',
        danger: 'bg-danger text-brand-on-primary hover:opacity-90',
        'danger-outline': 'border border-danger bg-surface-default text-danger hover:bg-danger-bg',
        link: 'h-auto px-0 text-brand-primary underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-8 px-3 text-helper',
        md: 'h-10 px-4 text-body',
        lg: 'h-11 px-5 text-body',
      },
    },
    defaultVariants: { variant: 'primary', size: 'md' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  /** Shows a spinner, sets aria-busy and disables the button. */
  loading?: boolean;
  /** Render the child element (e.g. a router Link) with button styles. */
  asChild?: boolean;
  leadingIcon?: React.ReactNode;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { className, variant, size, loading, asChild, leadingIcon, children, disabled, type, ...props },
  ref,
) {
  const classes = cn(buttonVariants({ variant, size }), variant === 'link' && 'h-auto', className);
  if (asChild) {
    return (
      <Slot.Root ref={ref} className={classes} {...props}>
        {children}
      </Slot.Root>
    );
  }
  return (
    <button
      ref={ref}
      type={type ?? 'button'}
      className={classes}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? <Loader2 aria-hidden className="animate-spin" /> : leadingIcon}
      {children}
    </button>
  );
});

export interface IconButtonProps extends Omit<ButtonProps, 'leadingIcon' | 'children'> {
  /** Required accessible name. */
  label: string;
  icon: React.ReactNode;
}

export const IconButton = React.forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  { label, icon, className, size = 'md', variant = 'ghost', ...props },
  ref,
) {
  return (
    <Button
      ref={ref}
      aria-label={label}
      title={label}
      variant={variant}
      size={size}
      className={cn(size === 'sm' ? 'w-8 px-0' : size === 'lg' ? 'w-11 px-0' : 'w-10 px-0', className)}
      {...props}
    >
      {icon}
    </Button>
  );
});
